import { Dispatch, SetStateAction, useState } from 'react';

import { copyTreeToNodes } from '../../utils/copy-tree-to-nodes';
import { determineNearestChildNode } from '../../utils/determineNearestChildNode';
import { FlatNode } from './types';
import { CopyCutPaste } from './use-mindmap-copy-cut-paste';
import { Data } from './use-mindmap-data';
import { Positioning } from './use-mindmap-positioning';
import { Selection } from './use-mindmap-selection';

const NEAREST_NODE_LENGTH_TRESHOLD = 150;
const SECOND_NEAREST_NODE_LENGTH_TRESHOLD = 75;

type DropPreviewNode = {
  id?: string;
  position?: 'left' | 'right' | 'below';
};

export interface DragAndDrop {
  dropPreviewNode: DropPreviewNode | undefined;
  isAnyNodeDragging: boolean;
  setIsAnyNodeDragging: Dispatch<SetStateAction<boolean>>;
  isRootNodeDragging: boolean;
  setIsRootNodeDragging: Dispatch<SetStateAction<boolean>>;
  dropDraggingElements: () => boolean;
  onPointerMove: (cursorX: number, cursorY: number) => void;
}

type UseDragAndDrop = { data: Data; positioning: Positioning; selection: Selection; copyCutPaste: CopyCutPaste };

export const useMindmapDragAndDrop = ({
  data: { nodeMap },
  positioning: { positionedTree },
  selection: { selectedNodeIds, unselectNodes },
  copyCutPaste: { getTreesOfNodeIds, pasteNodes },
}: UseDragAndDrop): DragAndDrop => {
  const [dropPreviewNode, setDropPreviewNode] = useState<DropPreviewNode>();
  const [isAnyNodeDragging, setIsAnyNodeDragging] = useState<boolean>(false);
  const [isRootNodeDragging, setIsRootNodeDragging] = useState<boolean>(false);

  const onPointerMove = (cursorX: number, cursorY: number) => {
    if (!positionedTree) return;
    if (!isAnyNodeDragging) {
      if (dropPreviewNode?.id) setDropPreviewNode({});
      return;
    }

    // get an array with all nodes and their positions on the map
    // while ignoring nodes that are beeing dragged and their children because
    // you shouldn't be able to drop into those and because they should be ignored while searching for the nearest nodes
    const flatPositionedNodes = copyTreeToNodes([positionedTree], {}, { keepIds: true, ignoreIds: selectedNodeIds });

    // search which nodes are the nearest to the cursor
    const { nearestNode, secondNearestNode } = determineNearestNodes(flatPositionedNodes, cursorX, cursorY);

    // determine where the drop preview node should be placed according to the cursor position and nearest nodes
    const newDropPreviewNode = determineDropPreviewNode(
      flatPositionedNodes,
      nearestNode,
      secondNearestNode,
      cursorX,
      cursorY,
    );

    if (!newDropPreviewNode?.id) {
      // preview node is out of reach
      if (dropPreviewNode?.id) {
        // only set preview node to {} if it isn't already {}
        setDropPreviewNode({});
        return;
      }
    }

    if (newDropPreviewNode?.id != dropPreviewNode?.id || newDropPreviewNode?.position != dropPreviewNode?.position) {
      // preview node has changed
      setDropPreviewNode(newDropPreviewNode);
    }
  };

  const determineNearestNodes = (flatPositionedNodes: FlatNode[], cursorX: number, cursorY: number) => {
    let secondNearestNodeLength = Infinity;
    let secondNearestNode: FlatNode | undefined = undefined;
    let reachedLengthTreshold = false;
    let reachedSecondLengthTreshold = false;

    // prepare to compare the last node to itself to let it calculate if it has reached the threshold
    flatPositionedNodes = [...flatPositionedNodes, flatPositionedNodes[0]];

    // find the nearest nodes
    let nearestNode: FlatNode | undefined = flatPositionedNodes.reduce((current, next) => {
      if (current.x !== undefined && current.y !== undefined && next.x !== undefined && next.y !== undefined) {
        const currentLeft = current.x;
        const currentRight = current.x + current.width;
        const currentTop = current.y;
        const currentBottom = current.y + current.height;

        const nextLeft = next.x;
        const nextRight = next.x + next.width;
        const nextTop = next.y;
        const nextBottom = next.y + next.height;

        // calculate lengths from the mouse cursor to the border of the nodes
        const isBelowNode = cursorY >= currentBottom;
        const isAboveNode = cursorY <= currentTop;
        const isRightToNode = cursorX >= currentRight;
        const isLeftToNode = cursorX <= currentLeft;
        const diffCurrent = {
          x: isRightToNode ? cursorX - currentRight : isLeftToNode ? currentLeft - cursorX : 0,
          y: isBelowNode ? cursorY - currentBottom : isAboveNode ? currentTop - cursorY : 0,
        };
        const diffNext = {
          x: cursorX >= nextRight ? cursorX - nextRight : cursorX <= nextLeft ? nextLeft - cursorX : 0,
          y: cursorY >= nextBottom ? cursorY - nextBottom : cursorY <= nextTop ? nextTop - cursorY : 0,
        };

        const currentLength = Math.sqrt(diffCurrent.x * diffCurrent.x + diffCurrent.y * diffCurrent.y);
        const nextLength = Math.sqrt(diffNext.x * diffNext.x + diffNext.y * diffNext.y);

        if (currentLength < NEAREST_NODE_LENGTH_TRESHOLD) {
          reachedLengthTreshold = true;
        }

        if (currentLength < nextLength) {
          // check if nextLength could be second nearest one
          if (nextLength < secondNearestNodeLength) {
            secondNearestNode = next;
            secondNearestNodeLength = nextLength;
          }

          return current;
        } else {
          // check if currentLength could be second nearest one
          if (currentLength < secondNearestNodeLength) {
            secondNearestNode = current;
            secondNearestNodeLength = currentLength;
          }

          return next;
        }
      }

      return next;
    });

    if (secondNearestNode && (secondNearestNode as FlatNode).id == nearestNode.id) {
      // second nearest node can't be nearest node at the same time
      // (e.g. when root node was detected as nearest and second nearest node)
      secondNearestNode = undefined;
    }

    if (secondNearestNodeLength < SECOND_NEAREST_NODE_LENGTH_TRESHOLD) {
      reachedSecondLengthTreshold = true;
    }

    if (!reachedSecondLengthTreshold) {
      secondNearestNode = undefined;
    }
    if (!reachedLengthTreshold) {
      nearestNode = undefined;
    }

    return { nearestNode, secondNearestNode };
  };

  const determineDropPreviewNode = (
    flatPositionedNodes: FlatNode[],
    nearestNode: FlatNode | undefined,
    secondNearestNode: FlatNode | undefined,
    cursorX: number,
    cursorY: number,
  ): DropPreviewNode => {
    let dropPreviewNode = {};

    if (nearestNode && nearestNode.x !== undefined && nearestNode.y !== undefined) {
      const isBelowNearestNode = cursorY >= nearestNode.y + nearestNode.height;
      const isLeftNearestNode = cursorX <= nearestNode.x + nearestNode.width / 2;

      if (isBelowNearestNode && secondNearestNode && secondNearestNode.x) {
        // insert into node's children (left or right from secondNearestNode)
        const isLeftFromSecondNearestNode = cursorX <= secondNearestNode.x + secondNearestNode.width / 2;
        if (isLeftFromSecondNearestNode) {
          // it's left from the nearest child
          dropPreviewNode = { id: secondNearestNode.id, position: 'left' };
        } else {
          // it's right from the nearest child
          dropPreviewNode = { id: secondNearestNode.id, position: 'right' };
        }
      } else if (isBelowNearestNode) {
        const children = flatPositionedNodes.filter(({ pid }) => pid == nearestNode.id);

        if (children.length > 0) {
          // there is a empty place below the nearest node
          // between the children of it that are not recognized as secondNearestNode
          // determine which child is closest to the cursor
          const { closestChild, side } = determineNearestChildNode({ children, cursorX });

          dropPreviewNode = { id: closestChild?.id, position: side };
        } else {
          // nearestNode doesn't has any children. insert as the first node's child
          dropPreviewNode = { id: nearestNode.id, position: 'below' };
        }
      } else if (isLeftNearestNode) {
        // directly left from nearest node
        dropPreviewNode = { id: nearestNode.id, position: 'left' };
      } else {
        // directly right from nearest node
        dropPreviewNode = { id: nearestNode.id, position: 'right' };
      }
    }

    return dropPreviewNode;
  };

  const dropDraggingElements = () => {
    let resetPosition = true;

    // check if there is place to drop the node
    if (dropPreviewNode && nodeMap) {
      const trees = getTreesOfNodeIds(selectedNodeIds);

      if (dropPreviewNode?.id) {
        const { position } = dropPreviewNode;
        // default position is below
        let insertAtId = dropPreviewNode.id;
        if (position == 'left' || position == 'right') {
          insertAtId = nodeMap[dropPreviewNode.id].pid ?? insertAtId;
        }
        const newNodeMap = pasteNodes(trees, [insertAtId], { keepIds: true }); // TODO: insert at specific place of children
        if (newNodeMap) {
          // reset position back into original place if either some PIDs or some child order position has changed
          const newNodesMapValues = Object.values(newNodeMap);
          for (const newNode of newNodesMapValues) {
            const oldNode = nodeMap[newNode.id];
            const parentChanged = newNode.pid !== oldNode.pid;
            if (parentChanged) {
              resetPosition = false;
              break;
            }
          }
        }
      }
    }

    unselectNodes();

    // reset the position if there were no changes to the map (e.g. a node was dropped into its origin position)
    return resetPosition;
  };

  return {
    onPointerMove,
    dropPreviewNode,
    dropDraggingElements,
    isAnyNodeDragging,
    setIsAnyNodeDragging,
    isRootNodeDragging,
    setIsRootNodeDragging,
  };
};
